/**
 * Hook para resaltar visualmente una fila recién creada/editada.
 * Uso:
 *   const { highlightId, highlightRow, scrollHighlightedIntoView } = useHighlight();
 *   <tr ref={highlightRow(item.id)} className={highlightId === item.id ? "row-highlight" : ""}>
 *
 * Al ejecutar `highlightId = saved.id` (via setHighlightId), la fila se marca con
 * la clase CSS y se hace scroll automático hacia ella. El highlight se quita
 * automáticamente a los 2.5s.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export function useRowHighlight(durationMs = 2500) {
  const [highlightId, setHighlightId] = useState(null);
  const refs = useRef(new Map());

  const setRef = useCallback((id) => (node) => {
    if (!id) return;
    if (node) refs.current.set(id, node);
    else refs.current.delete(id);
  }, []);

  const flash = useCallback((id) => {
    if (!id) return;
    setHighlightId(id);
    // Scroll después del render
    setTimeout(() => {
      const node = refs.current.get(id);
      if (node && typeof node.scrollIntoView === "function") {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50);
  }, []);

  useEffect(() => {
    if (!highlightId) return undefined;
    const t = setTimeout(() => setHighlightId(null), durationMs);
    return () => clearTimeout(t);
  }, [highlightId, durationMs]);

  return { highlightId, setRef, flash };
}
